import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Store, Loader2, ArrowRight, Mail, Lock, User as UserIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const GoogleIcon = (props) => (
  <svg viewBox="0 0 24 24" width="18" height="18" {...props}>
    <path fill="#FFC107" d="M43.6 20.5h-1.9V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.9 4.1 29.7 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22 22-9.8 22-22c0-1.5-.2-2.6-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.9 4.1 29.7 2 24 2 15.6 2 8.3 6.9 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 46c5.6 0 10.7-2.1 14.6-5.6l-6.7-5.7C29.9 36.3 27.1 37 24 37c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C8.2 41 15.5 46 24 46z"/>
    <path fill="#1976D2" d="M43.6 20.5H24v8h11.3c-.8 2.3-2.2 4.2-4.1 5.6l6.7 5.7C41.9 36.9 46 31 46 24c0-1.5-.2-2.6-.4-3.5z"/>
  </svg>
);

export default function Login() {
  const { login, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '' });

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await login(form.email, form.password); toast.success('Welcome back!'); navigate('/dashboard'); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Login failed'); }
    finally { setBusy(false); }
  };
  const doRegister = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await register(form.email, form.password, form.name); toast.success('Account created!'); navigate('/onboarding'); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Sign up failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="luxe-bg flex min-h-screen w-full items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-6 flex items-center justify-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground"><Store className="h-5 w-5" /></span>
          <span className="font-display text-xl font-bold text-white">BioCloser</span>
        </Link>

        <div className="glass rounded-3xl p-7">
          <Button onClick={loginWithGoogle} variant="outline" className="mb-5 h-11 w-full gap-2 rounded-xl border-white/15 bg-white/[0.04] text-white hover:bg-white/[0.08]">
            <GoogleIcon /> Continue with Google
          </Button>
          <div className="mb-5 flex items-center gap-3 text-xs text-muted-foreground">
            <div className="h-px flex-1 bg-white/10" /> or use email <div className="h-px flex-1 bg-white/10" />
          </div>

          <Tabs defaultValue="login">
            <TabsList className="mb-4 grid w-full grid-cols-2 rounded-full bg-white/[0.04] p-1">
              <TabsTrigger value="login" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Log in</TabsTrigger>
              <TabsTrigger value="register" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Sign up</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={doLogin} className="space-y-3">
                <Field icon={Mail} label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} placeholder="you@email.com" />
                <Field icon={Lock} label="Password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} placeholder="••••••••" />
                <Button type="submit" disabled={busy} className="h-11 w-full rounded-xl bg-primary font-bold text-primary-foreground hover:bg-primary/90 glow-lime">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Log in <ArrowRight className="ml-1.5 h-4 w-4" /></>}
                </Button>
                <p className="text-center text-xs text-muted-foreground">Demo: aria@biocloser.com / demo1234</p>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={doRegister} className="space-y-3">
                <Field icon={UserIcon} label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Jane Creator" />
                <Field icon={Mail} label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} placeholder="you@email.com" />
                <Field icon={Lock} label="Password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} placeholder="Create a password" />
                <Button type="submit" disabled={busy} className="h-11 w-full rounded-xl bg-primary font-bold text-primary-foreground hover:bg-primary/90 glow-lime">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Create account <ArrowRight className="ml-1.5 h-4 w-4" /></>}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, type = 'text', value, onChange, placeholder }) {
  return (
    <div>
      <Label className="mb-1 block text-xs text-muted-foreground">{label}</Label>
      <div className="relative">
        <Icon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="rounded-xl border-white/10 bg-white/[0.04] pl-9 focus-visible:ring-primary" required />
      </div>
    </div>
  );
}
